import { useCallback, useEffect, useRef, useState } from 'react';
import { reportGameOver, reportPlay } from './beacon';
import { EtooLogo } from './EtooLogo';
import { BreakerEngine } from './game/engine';
import { bindKeys } from './game/input';
import { LEVELS } from './game/levels';
import type { HudSnapshot } from './game/types';

const EMPTY: HudSnapshot = {
  phase: 'menu',
  level: 1,
  levelName: 'Tutorial',
  subtitle: '',
  score: 0,
  highScore: 0,
  lives: 3,
  bricksLeft: 0,
  combo: 0,
  multiplier: 1,
  activePowers: [],
  muted: false,
  levelBonus: 0,
  lifeBonus: 0,
  isNewHigh: false,
  serving: true,
  unlocked: 0,
};

export default function App() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<BreakerEngine | null>(null);
  const [hud, setHud] = useState<HudSnapshot>(EMPTY);
  const [howTo, setHowTo] = useState(false);
  const playSent = useRef(false);
  const overSent = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const engine = new BreakerEngine(ctx, { onHud: setHud });
    engineRef.current = engine;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      engine.resize(w, h, dpr);
    };
    resize();
    window.addEventListener('resize', resize);
    engine.start();

    const unbind = bindKeys(
      (code, down) => engine.setKey(code, down),
      () => {
        if (engine.phase === 'playing') engine.pause();
        else if (engine.phase === 'paused') engine.resume();
      },
      () => engine.launchOrAction(),
    );

    return () => {
      window.removeEventListener('resize', resize);
      unbind();
      engine.destroy();
      engineRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (hud.phase === 'levelStart' && !playSent.current) {
      playSent.current = true;
      reportPlay();
    }
    if (hud.phase === 'menu') {
      playSent.current = false;
      overSent.current = false;
    }
    if ((hud.phase === 'gameOver' || hud.phase === 'victory') && !overSent.current && engineRef.current) {
      overSent.current = true;
      reportGameOver(performance.now() - engineRef.current.runStartedAt);
    }
  }, [hud.phase, hud.level]);

  const pointer = useCallback((e: React.PointerEvent) => {
    engineRef.current?.setPointer(e.clientX);
  }, []);

  const mute = useCallback(() => engineRef.current?.toggleMute(), []);

  return (
    <div className="app">
      <canvas
        ref={canvasRef}
        className="board"
        onPointerMove={pointer}
        onPointerDown={(e) => {
          pointer(e);
          const engine = engineRef.current;
          if (!engine) return;
          if (engine.phase === 'lifeLost' || engine.hud().serving) engine.launchOrAction();
        }}
        onPointerUp={() => engineRef.current?.setPointer(null)}
        onPointerLeave={() => engineRef.current?.setPointer(null)}
        aria-label="Neon Breaker playfield"
      />

      {(hud.phase === 'playing' || hud.phase === 'paused' || hud.phase === 'levelStart' || hud.phase === 'lifeLost') && (
        <header className="hud">
          <Stat label="Level" value={`${hud.level}`} />
          <Stat label="Score" value={hud.score.toLocaleString()} />
          <Stat label="Best" value={hud.highScore.toLocaleString()} />
          <Stat label="Lives" value={'●'.repeat(Math.max(0, hud.lives))} />
          <Stat label="Bricks" value={`${hud.bricksLeft}`} />
          {hud.combo > 1 && <Stat label="Combo" value={`${hud.combo}×${hud.multiplier.toFixed(2)}`} />}
        </header>
      )}

      {hud.activePowers.length > 0 && hud.phase === 'playing' && (
        <ul className="chips">
          {hud.activePowers.map((p) => (
            <li key={p.id} className="chip">
              {p.label}
              {p.remainingMs > 0 ? ` ${Math.ceil(p.remainingMs / 1000)}s` : ''}
            </li>
          ))}
        </ul>
      )}

      {hud.phase === 'playing' && hud.serving && <p className="serve-hint">Space or tap to launch</p>}

      {hud.phase === 'menu' && (
        <Overlay>
          <p className="kicker">etoo arcade</p>
          <h1>NEON BREAKER</h1>
          <p className="lede">Ten handcrafted levels. Twelve power-ups. One last fortress.</p>
          <p className="best">Best {hud.highScore.toLocaleString()}</p>
          <div className="row">
            <button className="btn primary" onClick={() => engineRef.current?.openLevelSelect()}>Play</button>
            <button className="btn" onClick={() => setHowTo(true)}>How to play</button>
            <button className="btn" onClick={mute}>{hud.muted ? 'Unmute' : 'Mute'}</button>
          </div>
        </Overlay>
      )}

      {hud.phase === 'levelSelect' && (
        <Overlay wide>
          <p className="kicker">campaign</p>
          <h2>Select a level</h2>
          <p className="lede">Start anywhere. Clear it and you’ll keep going to the next.</p>
          <ol className="level-grid">
            {LEVELS.map((level) => (
              <li key={level.id}>
                <button
                  className={`level-btn ${hud.unlocked >= level.id ? 'cleared' : ''}`}
                  onClick={() => engineRef.current?.startGame(level.id)}
                >
                  <span className="level-num">{level.id}</span>
                  <span className="level-name">{level.name}</span>
                </button>
              </li>
            ))}
          </ol>
          <div className="row">
            <button className="btn" onClick={() => engineRef.current?.goMenu()}>Back</button>
          </div>
        </Overlay>
      )}

      {howTo && (
        <Overlay dim>
          <h2>How to play</h2>
          <ul className="help">
            <li>Move with mouse, touch, arrows, or A/D.</li>
            <li>Space or tap launches the ball. Esc / P pauses.</li>
            <li>Clear every destructible brick to advance.</li>
            <li>Steel bricks bounce forever. Explosive bricks take neighbors with them.</li>
            <li>Catch falling icons. Combos multiply consecutive breaks.</li>
          </ul>
          <button className="btn primary" onClick={() => setHowTo(false)}>Got it</button>
        </Overlay>
      )}

      {hud.phase === 'levelStart' && (
        <Overlay ghost>
          <p className="kicker">Level {hud.level}</p>
          <h2>{hud.levelName}</h2>
          <p className="lede">{hud.subtitle}</p>
        </Overlay>
      )}

      {hud.phase === 'paused' && (
        <Overlay>
          <h2>Paused</h2>
          <div className="row">
            <button className="btn primary" onClick={() => engineRef.current?.resume()}>Resume</button>
            <button className="btn" onClick={() => engineRef.current?.restartLevel()}>Restart level</button>
            <button className="btn" onClick={() => engineRef.current?.openLevelSelect()}>Levels</button>
            <button className="btn" onClick={() => engineRef.current?.goMenu()}>Menu</button>
            <button className="btn" onClick={mute}>{hud.muted ? 'Unmute' : 'Mute'}</button>
          </div>
        </Overlay>
      )}

      {hud.phase === 'lifeLost' && (
        <Overlay ghost>
          <h2>Ball down</h2>
          <p className="lede">{hud.lives} {hud.lives === 1 ? 'life' : 'lives'} left. Space to serve.</p>
        </Overlay>
      )}

      {hud.phase === 'levelComplete' && (
        <Overlay>
          <p className="kicker">Cleared</p>
          <h2>{hud.levelName}</h2>
          <p className="lede">Level bonus {hud.levelBonus} · Lives bonus {hud.lifeBonus}</p>
          <button className="btn primary" onClick={() => engineRef.current?.launchOrAction()}>
            {hud.level >= LEVELS.length ? 'Finish' : `Next: ${LEVELS[hud.level]?.name ?? 'Level ' + (hud.level + 1)}`}
          </button>
          <div className="row" style={{ marginTop: 10 }}>
            <button className="btn" onClick={() => engineRef.current?.openLevelSelect()}>Level select</button>
          </div>
        </Overlay>
      )}

      {hud.phase === 'gameOver' && (
        <Overlay>
          <h2>Game over</h2>
          <p className="lede">Score {hud.score.toLocaleString()}</p>
          {hud.isNewHigh && <p className="best">New best {hud.highScore.toLocaleString()}</p>}
          <div className="row">
            <button className="btn primary" onClick={() => engineRef.current?.startGame(engineRef.current.runStartLevel)}>Play again</button>
            <button className="btn" onClick={() => engineRef.current?.openLevelSelect()}>Levels</button>
            <button className="btn" onClick={() => engineRef.current?.goMenu()}>Menu</button>
          </div>
        </Overlay>
      )}

      {hud.phase === 'victory' && (
        <Overlay>
          <p className="kicker">Finale cleared</p>
          <h2>You broke the neon</h2>
          <p className="lede">Final score {hud.score.toLocaleString()}</p>
          {hud.isNewHigh && <p className="best">New best saved on this device</p>}
          <div className="row">
            <button className="btn primary" onClick={() => engineRef.current?.startGame(engineRef.current.runStartLevel)}>Play again</button>
            <button className="btn" onClick={() => engineRef.current?.openLevelSelect()}>Levels</button>
            <button className="btn" onClick={() => engineRef.current?.goMenu()}>Menu</button>
          </div>
        </Overlay>
      )}

      <button className="mute-fab" onClick={mute} aria-label="Toggle sound">
        {hud.muted ? 'Muted' : 'Sound'}
      </button>
      <EtooLogo />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Overlay({ children, ghost, dim, wide }: { children: React.ReactNode; ghost?: boolean; dim?: boolean; wide?: boolean }) {
  return (
    <div className={`overlay ${ghost ? 'ghost' : ''} ${dim ? 'dim' : ''}`}>
      <div className={`card ${wide ? 'wide' : ''}`}>{children}</div>
    </div>
  );
}
